import { maskVendor } from "@/shared/lib/vendorMask";
import type { VideoAssetDto } from "../api/videoService";
import { shotEditForm, type ShotEditForm, type ShotTexts } from "./shotEditForm";

/**
 * 失败段「改文案再重渲」的编排规则(VID-D,2026-08-18 老板走查:
 * 「各分段生成中经常有失败的段,可以重新渲染,但没有地方去改文案」)。
 *
 * <p>一段含多镜,而编辑接口是**逐镜**的 —— 所以「保存并重渲本段」= N 次 editShot + 1 次
 * regenerateChunk。此文件只管**顺序与前提**,不碰网络。
 */

/** 一段里可编辑的那几镜(全局 1-based 镜号 + 各自的文案)。 */
export interface ChunkEditDraft {
  seq: number;
  shots: { shotSeq: number; form: ShotEditForm; origin: ShotEditForm }[];
  /** 这一段为什么失败(已翻成人话);成功段为 null。**跟着草稿走**,见下。 */
  failure: { reason: string; code?: string } | null;
}

/** 段 → 草稿:把该段含的每一镜的现有文案摊开成可编辑的表单。 */
export function chunkEditDraft(chunk: VideoAssetDto, texts: ShotTexts): ChunkEditDraft {
  const seqs = chunk.shotSeqs ?? [];
  const failed = chunk.status === "FAILED" || chunk.status === "BLOCKED";
  return {
    seq: chunk.seq,
    shots: seqs.map((shotSeq) => {
      const form = shotEditForm(texts, shotSeq);
      return { shotSeq, form, origin: form };
    }),
    // ★原因要**跟着编辑面板走**:它开在网格上方,而失败横幅在下面的段卡里 ——
    //   用户一点「改文案」,那句"为什么失败"就滚出视野了,而它正是他此刻要照着改的东西。
    failure: failed ? chunkFailureHint(chunk) : null,
  };
}

/**
 * 哪些镜真被改过 —— 只对这些镜发 editShot。
 * 全量发会把「这一镜本来就没有动作描述」写成空串(用空值覆盖,与预填错源同族)。
 */
export function changedShots(draft: ChunkEditDraft) {
  return draft.shots.filter((s) =>
    (Object.keys(s.origin) as (keyof ShotEditForm)[]).some((k) => s.form[k] !== s.origin[k])
  );
}

export function chunkEditDirty(draft: ChunkEditDraft): boolean {
  return changedShots(draft).length > 0;
}

/**
 * 段能不能改文案再重渲。
 *
 * <p>★还在跑的段(PENDING/RUNNING)不给 —— 「重渲一个正在渲的东西」是句病句,而且真会
 * 开一张计费 ticket 去重复渲染同一段(VideoChunkGrid V4 记过这条)。
 */
export function chunkEditable(chunk: Pick<VideoAssetDto, "status" | "shotSeqs">): boolean {
  if (chunk.status === "PENDING" || chunk.status === "RUNNING") return false;
  return (chunk.shotSeqs ?? []).length > 0;
}

/**
 * 失败段该给什么提示。
 *
 * <p>★后端认不出原因时回的是「原因还没查清」,这里**不许**在前端再补一个猜测 ——
 * 同一条链上两处各猜一次,用户会拿到一个听起来很确定的错答案。
 */
export function chunkFailureHint(chunk: Pick<VideoAssetDto, "failureReason" | "errorCode">): {
  reason: string;
  code?: string;
} {
  const reason = (maskVendor(chunk.failureReason) ?? "").trim();
  return {
    reason: reason || "这一段没能生成,原因还没查清 —— 改一改文案再试一次通常能过。",
    // 码给排查用(可复制给运营),但不当主文案:用户看不懂它,也不该看懂
    code: chunk.errorCode || undefined,
  };
}

/**
 * 「保存并重渲」的费用说法。★两句必须分开:只保存**不花钱**,重渲**要花钱**。
 * 用户报的现象就是「能重渲但没处改文案」—— 修完之后最容易犯的下一个错,是让他以为
 * 改个错别字也要重新扣一次。
 */
export const CHUNK_EDIT_COST_HINT = {
  saveOnly: "只改文案不花积分;下次合成成片时口播会按新文案来。",
  saveAndRender: "重渲这一段会按段计费(和第一次生成同价),画面会整段重出。",
} as const;
